import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { RotateCcw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Input } from './ui/input';

/*
 * The numbers on one partner's proposal, before it is sent.
 *
 * Used by BOTH places a proposal goes out — approving a new applicant, and re-sending to an
 * existing partner — because the two must offer the same fields in the same shape. A second
 * copy of this form is a second set of numbers that drifts.
 *
 * What it emits (`onChange`) is a SPARSE DIFF: only the fields actually touched, each with the
 * reason for it. Untouched fields are absent rather than sent at their current value, so a
 * proposal that changes one number records one change — and the reason has somewhere to live.
 *
 * Nothing here validates a value. Every commercial number is negotiable, and a figure typed on
 * purpose is a decision rather than an error. What keeps it honest is that the change is
 * visible: each edited field shows what it was, and the count at the top says how many differ.
 *
 * PERCENTAGES ARE STORED AS FRACTIONS. The card holds 0.15; a person types 15. The conversion
 * lives here and nowhere else — sending 15 where 0.15 was meant is a 1500% commission, and it
 * is the one mistake in this form that money would actually follow.
 */

const PCT = 'pct';
const RUPEES = 'rupees';
const MONTHS = 'months';

const FIELDS = [
  { group: 'Reseller — credits', rows: [
    { path: 'wallet.floor', label: 'Minimum purchase', unit: RUPEES,
      hint: 'The gate. Below it a partner earns more on commission.' },
    { path: 'wallet.discount.silver', label: 'Discount — silver', unit: PCT },
    { path: 'wallet.discount.gold', label: 'Discount — gold', unit: PCT },
    { path: 'wallet.discount.platinum', label: 'Discount — platinum', unit: PCT },
    { path: 'wallet.min_client_seed', label: 'Client seed and refill', unit: RUPEES },
    { path: 'wallet.expiry_months', label: 'Expire in the wallet', unit: MONTHS },
    { path: 'wallet.client_expiry_months', label: 'Expire inside a client', unit: MONTHS },
  ]},
  { group: 'Reseller — white label', rows: [
    { path: 'whitelabel.cobrand_min_monthly', label: 'Co-brand minimum / month', unit: RUPEES },
    { path: 'whitelabel.cobrand_rate', label: 'Co-brand rate', unit: RUPEES },
    { path: 'whitelabel.full_min_monthly', label: 'Full white-label minimum / month', unit: RUPEES },
    { path: 'whitelabel.full_rate', label: 'Full white-label rate', unit: RUPEES },
  ]},
  { group: 'Commission — solution clients', rows: [
    { path: 'commission.solution.silver', label: 'Silver', unit: PCT },
    { path: 'commission.solution.gold', label: 'Gold', unit: PCT },
    { path: 'commission.solution.platinum', label: 'Platinum', unit: PCT },
  ]},
  { group: 'Commission — referral clients', rows: [
    { path: 'commission.referral.silver', label: 'Silver', unit: PCT },
    { path: 'commission.referral.gold', label: 'Gold', unit: PCT },
    { path: 'commission.referral.platinum', label: 'Platinum', unit: PCT },
  ]},
  { group: 'How partners climb', rows: [
    { path: 'tier_thresholds.gold', label: 'Gold from (monthly revenue)', unit: RUPEES },
    { path: 'tier_thresholds.platinum', label: 'Platinum from (monthly revenue)', unit: RUPEES },
  ]},
];

const at = (card, path) => path.split('.').reduce(
  (node, key) => (node && typeof node === 'object' ? node[key] : undefined), card);

const toDisplay = (value, unit) => {
  if (value === null || value === undefined) return '';
  return unit === PCT ? String(Math.round(value * 10000) / 100) : String(value);
};
const toStored = (raw, unit) => {
  const n = parseFloat(raw);
  if (isNaN(n)) return null;
  return unit === PCT ? n / 100 : n;
};

const ProposalTermsEditor = ({ partnerId, onChange }) => {
  const { getAuthHeaders, API_URL } = useAuth();
  const [loaded, setLoaded] = useState(null);
  const [edits, setEdits] = useState({});   // path -> {value, reason}
  const [error, setError] = useState('');

  useEffect(() => {
    if (!partnerId) return;
    (async () => {
      try {
        const res = await axios.get(
          `${API_URL}/api/admin/partners/${partnerId}/rate-card`, { headers: getAuthHeaders() });
        setLoaded(res.data);
      } catch {
        setError('Could not load this partner’s rates.');
      }
    })();
  }, [partnerId, API_URL, getAuthHeaders]);

  const emit = useCallback((next) => {
    setEdits(next);
    // Only fields with a real value reach the proposal. A cleared box is "leave it alone",
    // not "set it to nothing".
    onChange(Object.fromEntries(
      Object.entries(next).filter(([, e]) => e.value !== null && e.value !== undefined)));
  }, [onChange]);

  const setField = (path, unit, raw) => {
    const next = { ...edits };
    if (raw === '') delete next[path];
    else next[path] = { ...(next[path] || {}), value: toStored(raw, unit) };
    emit(next);
  };
  const setReason = (path, reason) => emit({ ...edits, [path]: { ...(edits[path] || {}), reason } });

  const resetAll = () => emit({});
  const resetOne = (path) => { const next = { ...edits }; delete next[path]; emit(next); };

  if (error) return <p className="text-sm text-[#B94A33]">{error}</p>;
  if (!loaded) return <p className="text-sm text-[#64748B]">Loading their rates…</p>;

  const changedCount = Object.keys(edits).length;

  return (
    <div className="space-y-4" data-testid="proposal-terms-editor">
      <div className="flex items-start justify-between gap-3 rounded-lg bg-[#FAFAF8] border border-[#E2E2DF] p-3">
        <p className="text-xs text-[#64748B]">
          Values start at what this partner is on today
          {loaded.pinned_version ? ` (card v${loaded.pinned_version})` : ' (the published card)'}.
          Change only what you have agreed. Blank means leave it alone.
          {changedCount > 0 && (
            <strong className="text-[#1A1A1A]"> {changedCount} change{changedCount === 1 ? '' : 's'} on this proposal.</strong>
          )}
        </p>
        {changedCount > 0 && (
          <button type="button" onClick={resetAll} data-testid="reset-all-terms"
                  className="shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold text-[#64748B] hover:text-[#1A1A1A]">
            <RotateCcw className="w-3.5 h-3.5" /> Reset all to standard
          </button>
        )}
      </div>

      {FIELDS.map((section) => (
        <div key={section.group}>
          <p className="text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-2">
            {section.group}
          </p>
          <div className="space-y-2">
            {section.rows.map((row) => {
              const theirs = at(loaded.card, row.path);
              const standard = at(loaded.base, row.path);
              const edited = edits[row.path];
              const shown = edited ? toDisplay(edited.value, row.unit) : toDisplay(theirs, row.unit);
              return (
                <div key={row.path} className="rounded-md border border-[#E2E2DF] p-2.5">
                  <div className="flex items-center gap-3">
                    <label className="flex-1 text-sm text-[#1A1A1A]" htmlFor={row.path}>
                      {row.label}
                      {row.hint && <span className="block text-xs text-[#64748B]">{row.hint}</span>}
                    </label>
                    <div className="flex items-center gap-1.5">
                      <Input
                        id={row.path}
                        data-testid={`term-${row.path}`}
                        type="number"
                        value={shown}
                        placeholder={toDisplay(standard, row.unit)}
                        onChange={(e) => setField(row.path, row.unit, e.target.value)}
                        className="h-9 w-28 border-[#E2E2DF] text-right tabular-nums"
                      />
                      <span className="w-12 text-xs text-[#64748B]">
                        {row.unit === PCT ? '%' : row.unit === MONTHS ? 'months' : '₹'}
                      </span>
                    </div>
                  </div>
                  {edited && (
                    <div className="mt-2 flex items-center gap-2">
                      <Input
                        value={edited.reason || ''}
                        onChange={(e) => setReason(row.path, e.target.value)}
                        placeholder="Why this rate? (kept internal — the partner never sees it)"
                        className="h-8 flex-1 border-[#E2E2DF] text-xs"
                        data-testid={`reason-${row.path}`}
                      />
                      <button type="button" onClick={() => resetOne(row.path)}
                              className="text-xs text-[#64748B] hover:text-[#1A1A1A] shrink-0">
                        Undo
                      </button>
                    </div>
                  )}
                  {edited && standard !== undefined && (
                    <p className="mt-1.5 text-xs text-[#B94A33]">
                      Standard is {toDisplay(standard, row.unit)}{row.unit === PCT ? '%' : ''}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default ProposalTermsEditor;
